import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { GraduationCap } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { GifWithPngFallback } from "@/components/media/GifWithPngFallback";

interface ToolItem {
  key: string;
  path: string; // path now targets spec explainer page
  loginRequired: boolean;
  heroSrcBase: string;
}

export function ToolkitSection() {
  const { t } = useTranslation(["newLanding"]);
  const carouselApiRef = useRef<CarouselApi | null>(null);
  const autoPlayTimer = useRef<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useRef<boolean>(false);
  const [firstVisibleIdx, setFirstVisibleIdx] = useState(0);
  const prevPausedRef = useRef(false);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
  }, []);

  const tick = useCallback(() => {
    if (isPaused || prefersReducedMotion.current) return;
    const api = carouselApiRef.current;
    if (!api) return;
    if (api.canScrollNext()) api.scrollNext();
    else api.scrollTo(0);
  }, [isPaused]);

  useEffect(() => {
    if (autoPlayTimer.current) window.clearInterval(autoPlayTimer.current);
    if (!isPaused && !prefersReducedMotion.current) {
      autoPlayTimer.current = window.setInterval(tick, 17000);
    }
    return () => {
      if (autoPlayTimer.current) window.clearInterval(autoPlayTimer.current);
    };
  }, [tick, isPaused]);

  const pause = () => setIsPaused(true);
  const resume = () => setIsPaused(false);

  const updateVisibility = useCallback(() => {
    const api = carouselApiRef.current;
    if (!api) return;
    const inView = api.slidesInView();
    if (inView.length > 0) {
      setFirstVisibleIdx(inView[0]);
    } else {
      setFirstVisibleIdx(api.selectedScrollSnap());
    }
  }, []);

  useEffect(() => {
    const handleVis = () => {
      const api = carouselApiRef.current;
      if (document.visibilityState === "hidden") {
        prevPausedRef.current = isPaused;
        if (autoPlayTimer.current) window.clearInterval(autoPlayTimer.current);
        setIsPaused(true);
      } else {
        api?.reInit();
        updateVisibility();
        setIsPaused(prevPausedRef.current);
      }
    };
    document.addEventListener("visibilitychange", handleVis);
    return () => document.removeEventListener("visibilitychange", handleVis);
  }, [isPaused, updateVisibility]);

  // Map tool keys to spec slugs used in ToolSpecPage
  const tools: ToolItem[] = useMemo(
    () => [
      {
        key: "lessonLab",
        path: "/lesson-lab",
        loginRequired: true,
        heroSrcBase: "/assets/lesson-lab",
      },
      {
        key: "communicationGenerator",
        path: "/writing-wizard",
        loginRequired: false,
        heroSrcBase: "/assets/writing-wizard",
      },
      {
        key: "researcher",
        path: "/researcher",
        loginRequired: false,
        heroSrcBase: "/assets/researcher",
      },
      {
        key: "resourceCustomiser",
        path: "/resource-refinery",
        loginRequired: true,
        heroSrcBase: "/assets/resource-refinery",
      },
      {
        key: "catchupCoach",
        path: "/catchup-coach",
        loginRequired: true,
        heroSrcBase: "/assets/catchup-coach",
      },
    ],
    []
  );

  // Track hover to override autoplay for any card
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section className="flex flex-col items-center w-full px-4 py-10 sm:py-14 md:py-16 bg-light-green">
      <GraduationCap
        className="mb-12 text-dark-green"
        size={40}
        weight="bold"
      />
      <h2 className="mb-4 text-2xl tracking-tight text-center sm:text-3xl md:text-4xl">
        {t("toolkit.title")}
      </h2>
      <p className="max-w-2xl mb-8 text-sm leading-relaxed text-center whitespace-pre-line text-secondary-foreground sm:text-base">
        {t("toolkit.subtitle")}
      </p>
      <div
        className="relative w-full px-2 mx-2 sm:px-4"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onFocus={pause}
        onBlur={resume}
      >
        <Carousel
          className="w-full"
          opts={{ align: "start", loop: true }}
          setApi={(api) => {
            carouselApiRef.current = api;
            if (api) {
              updateVisibility();
              api.on("select", () => {
                updateVisibility();
              });
              api.on("scroll", updateVisibility);
              api.on("reInit", updateVisibility);
            }
          }}
        >
          <CarouselContent>
            {tools.map((tool, idx) => (
              <CarouselItem
                key={tool.key}
                className="basis-[85%] xs:basis-[70%] sm:basis-1/2 xl:basis-1/3 2xl:basis-1/4 pr-4"
              >
                <Link
                  to={tool.path}
                  className="flex flex-col h-full overflow-hidden transition-shadow border-2 group rounded-2xl bg-card border-border hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <div className="relative w-full aspect-[4/3] sm:aspect-[5/3] bg-gradient-to-br from-primary/15 via-transparent to-secondary/25 flex items-center justify-center overflow-hidden">
                    <GifWithPngFallback
                      srcBase={tool.heroSrcBase}
                      alt={t(`toolkit.${tool.key}.title`)}
                      className="object-cover w-full h-full"
                      idleMode="title"
                      playing={
                        hoveredIdx == null &&
                        !isPaused &&
                        firstVisibleIdx === idx &&
                        !prefersReducedMotion.current
                      }
                      onHoverChange={(hover) =>
                        setHoveredIdx((prev) =>
                          hover ? idx : prev === idx ? null : prev
                        )
                      }
                    />
                    {!tool.loginRequired && (
                      <Badge
                        className="absolute top-3 left-3 text-[10px] px-2 py-0.5 shadow"
                        variant="secondary"
                      >
                        {t("toolkit.badge.noLogin")}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 gap-3 p-5">
                    <h3 className="text-base font-semibold leading-snug">
                      {t(`toolkit.${tool.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-5">
                      {t(`toolkit.${tool.key}.body`)}
                    </p>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden ml-8 md:flex" />
          <CarouselNext className="hidden mr-8 md:flex" />
        </Carousel>
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {tools.map((_, idx) => (
            <span
              key={idx}
              className="h-2 w-2 rounded-full bg-primary/30 data-[active=true]:bg-primary transition-colors"
              data-active={carouselApiRef.current?.selectedScrollSnap() === idx}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
